import { useNotifications } from '@revenuewell/uc-notifications-client';
import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { EventApptRequestsWidget, EventBus, EventGoToAppointments } from '../../types/event-bus';
import { useEventBus } from '../use-event-bus';
import { useHistory } from '../use-history-intercept';
import { useLocations } from '../use-locations';
import { useWrapperNavigation } from '../use-wrapper-navigation';
import { CalendarAppContext, ICalendarAppContext } from './context';

export interface CalendarAppProviderProps {
  children: ReactNode;
}

export const CalendarAppProvider: FC<CalendarAppProviderProps> = (props) => {
  const { listen, publish, publishToIframes } = useEventBus();
  const { listen: listenNotification } = useNotifications();
  const { selectedLocation } = useLocations();
  const history = useHistory();
  const { activeFrame, windowUrls } = useWrapperNavigation();

  useEffect(() => {
    if (!selectedLocation) return;

    const apptRequestCeateEvent = listenNotification('appointment-request-new', (event: any) => {
      const locId = event?.locationId || event?.locationid;

      if (locId != selectedLocation?.id) return;

      publishToIframes({ messageType: 'appt-request-refresh-calendar' });
    });

    return () => {
      apptRequestCeateEvent();
    };
  }, [listenNotification, selectedLocation]);

  const goToCalendarApp = useCallback(
    async (): Promise<void> =>
      new Promise((resolve) => {
        if (activeFrame !== 'scheduler') history.push(windowUrls.scheduler);

        if (history.location.pathname.includes('/calendar')) {
          resolve();

          return;
        }

        setTimeout(() => {
          publishToIframes({
            messageType: 'set-frame-path',
            data: {
              pathname: '/calendar'
            }
          });
          resolve();
        }, 500);
      }),
    [activeFrame, history, windowUrls, publishToIframes]
  );

  const handleGoToAppointments = useCallback(
    async (event: EventBus) => {
      const goToApptEvent = event as EventGoToAppointments;

      // First, navigate to the calendar
      await goToCalendarApp();

      // Then publish the appt-requests-widget event with filter data
      const apptRequestsWidgetEvent: EventApptRequestsWidget = {
        messageType: 'appt-requests-widget',
        data: {
          props: {
            filter: goToApptEvent.data.filter
          }
        }
      };

      publish(apptRequestsWidgetEvent);
    },
    [goToCalendarApp, publish]
  );

  useEffect(() => {
    return listen('go-to-appointments', handleGoToAppointments);
  }, [listen, handleGoToAppointments]);

  const contextValue: ICalendarAppContext = useMemo(
    () => ({
      goToCalendarApp
    }),
    [goToCalendarApp]
  );

  return <CalendarAppContext.Provider value={contextValue}>{props.children}</CalendarAppContext.Provider>;
};
